import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDownIcon, GlobeAltIcon } from '@heroicons/react/24/outline';
import { useLanguage } from '../../contexts/LanguageContext';

const LanguageSwitcher = ({ className = '', variant = 'default' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const { currentLanguage, switchLanguage, supportedLanguages, getCurrentLanguageInfo } = useLanguage();

  // 点击外部关闭下拉菜单
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const currentLangInfo = getCurrentLanguageInfo();

  // 处理语言切换
  const handleLanguageChange = (langCode) => {
    if (langCode !== currentLanguage) {
      setIsOpen(false);
      switchLanguage(langCode);
    }
  };

  // 不同变体的样式
  const variants = {
    default: {
      button: 'flex items-center gap-2 px-3 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-white border border-white/20 hover:border-white/30 transition-all duration-200',
      dropdown: 'absolute top-full right-0 mt-2 bg-white rounded-lg shadow-xl border border-gray-200 min-w-[140px] z-50',
      item: 'flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors duration-150 cursor-pointer'
    },
    navbar: {
      button: 'flex items-center gap-2 px-2 py-1 rounded-md hover:bg-gray-100 text-gray-700 transition-all duration-200',
      dropdown: 'absolute top-full right-0 mt-1 bg-white rounded-md shadow-lg border border-gray-200 min-w-[120px] z-50',
      item: 'flex items-center gap-2 px-3 py-2 hover:bg-gray-50 transition-colors duration-150 cursor-pointer text-sm'
    },
    mobile: {
      button: 'flex items-center gap-2 px-4 py-3 text-white hover:bg-white/10 transition-all duration-200 rounded-lg',
      dropdown: 'mt-2 bg-dark-800 rounded-lg border border-white/20 shadow-lg',
      item: 'flex items-center gap-3 px-4 py-3 hover:bg-white/10 transition-colors duration-150 cursor-pointer text-white'
    }
  };

  const currentVariant = variants[variant] || variants.default;

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {/* 语言切换按钮 */}
      <motion.button
        onClick={() => setIsOpen(!isOpen)}
        className={currentVariant.button}
        aria-label="切换语言"
        aria-expanded={isOpen}
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
      >
        <GlobeAltIcon className="w-4 h-4" />
        <span className="text-sm font-medium">
          {currentLangInfo.flag} {currentLangInfo.name}
        </span>
        <ChevronDownIcon
          className={`w-4 h-4 transition-transform duration-200 ${
            isOpen ? 'rotate-180' : ''
          }`}
        />
      </motion.button>

      {/* 下拉菜单 */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -5 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -5 }}
            transition={{ duration: 0.1 }}
            className={currentVariant.dropdown}
          >
            {Object.values(supportedLanguages).map((lang) => (
              <div
                key={lang.code}
                onClick={() => handleLanguageChange(lang.code)}
                className={`${currentVariant.item} ${
                  currentLanguage === lang.code
                    ? variant === 'mobile' ? 'bg-primary-600/20 text-primary-300' : 'bg-primary-50 text-primary-600'
                    : variant === 'mobile' ? 'text-white' : 'text-gray-700'
                }`}
              >
                <span className="text-lg">{lang.flag}</span>
                <span className="font-medium">{lang.name}</span>
                {currentLanguage === lang.code && (
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    className="ml-auto w-2 h-2 bg-primary-500 rounded-full"
                  />
                )}
              </div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default LanguageSwitcher;
